import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Calendar, Clock, MapPin, Users, StickyNote, User, Mail, Phone, CalendarDays, Sparkles, Plus, Minus, Flower2, Candy, Wine, Cookie, GlassWater } from "lucide-react";
import { api } from "../lib/api";
import { TID } from "../lib/testIds";
import { useAuth } from "../context/AuthContext";
import { useBookingIntent } from "../lib/useBookingIntent";
import { useBlacklistCheck } from "../lib/useBlacklistCheck";
import VehicleGallery from "../components/VehicleGallery";
import SmokingFeeToggle from "../components/SmokingFeeToggle";

// Map addon category slugs to lucide icons for a small visual cue in the picker.
const ADDON_ICON = {
  flowers: Flower2,
  candy: Candy,
  chips: Cookie,
  alcohol: Wine,
  beverages: GlassWater,
};

export default function BookRide() {
  const { vehicleId } = useParams();
  const [searchParams] = useSearchParams();
  const eventId = searchParams.get("event");
  const promoCode = (searchParams.get("promo") || "").trim() || null;
  // Marketing / referral channel tag — normalises the `?promo=…` slug to
  // underscores for consistent backend filtering. Distinct from promoCode
  // which drives Stripe/pricing overrides.
  const promoChannel = promoCode ? promoCode.replace(/-/g, "_") : null;
  const navigate = useNavigate();
  const { user } = useAuth();
  const [vehicle, setVehicle] = useState(null);
  const [event, setEvent] = useState(null);
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    pickup: "",
    dropoff: "",
    date: "",
    time_hour: "10",
    time_min: "00",
    time_ampm: "AM",
    passengers: 1,
    hours: 3,
    notes: "",
    trip_purpose: "airport",
    payment_method: "card",
    payment_amount_mode: "full",
    smoking_fee_accepted: false,
  });
  const [estimate, setEstimate] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState(null);
  // Add-ons catalog + selections. `selectedAddons` is a plain object keyed by
  // item_id → qty (int, 1+). We keep it as an object (not array) so +/− steppers
  // stay O(1) and the payload builder converts to the {item_id, qty} array shape
  // the backend expects.
  const [addonsCatalog, setAddonsCatalog] = useState([]);
  const [selectedAddons, setSelectedAddons] = useState({});

  useEffect(() => {
    api.get("/addons/catalog")
      .then((r) => setAddonsCatalog(Array.isArray(r.data) ? r.data : []))
      .catch(() => setAddonsCatalog([]));
  }, []);

  // Turn the {item_id: qty} map into the array shape the backend expects.
  const addonsPayload = useMemo(() => (
    Object.entries(selectedAddons)
      .filter(([, q]) => Number(q) > 0)
      .map(([item_id, qty]) => ({ item_id, qty: Number(qty) }))
  ), [selectedAddons]);

  useEffect(() => {
    api.get(`/vehicles/${vehicleId}`).then((r) => setVehicle(r.data)).catch(() => navigate("/fleet"));
  }, [vehicleId, navigate]);

  useEffect(() => {
    if (!eventId) return;
    api.get(`/events/${eventId}`).then((r) => {
      setEvent(r.data);
      // Prefill dropoff with venue and date/time from the event
      const ev = r.data;
      const dt = new Date(ev.event_date);
      const iso = dt.toISOString();
      setForm((f) => ({
        ...f,
        dropoff: f.dropoff || ev.location || "",
        date: f.date || iso.slice(0, 10),
        // Parse HH:MM (24h) from ISO into our 12h picker parts.
        time_hour: (() => {
          const h24 = Number(iso.slice(11, 13));
          const h12 = ((h24 + 11) % 12) + 1;
          return String(h12);
        })(),
        time_min: iso.slice(14, 16),
        time_ampm: Number(iso.slice(11, 13)) >= 12 ? "PM" : "AM",
        notes: f.notes || `Chauffeured ride to ${ev.title}${ev.location ? ` at ${ev.location}` : ""}.`,
      }));
    }).catch(() => {});
  }, [eventId]);

  useEffect(() => {
    if (user && !form.email) {
      setForm((f) => ({ ...f, full_name: user.name || "", email: user.email || "", phone: user.phone || "" }));
    }
  }, [user]);

  useEffect(() => {
    if (!vehicle) return;
    api.post("/estimates/ride", {
      vehicle_id: vehicle.id,
      pickup: form.pickup || "-",
      dropoff: form.dropoff || "-",
      estimated_duration_hours: Number(form.hours) || 1,
      passenger_count: Number(form.passengers) || 1,
      promo_code: promoCode,
      addons: addonsPayload,
      smoking_fee_accepted: form.smoking_fee_accepted,
    }).then((r) => setEstimate(r.data));
  }, [vehicle, form.hours, promoCode, addonsPayload, form.smoking_fee_accepted]);

  // Build a full 24h HH:MM string from the 3-part time picker.
  const timeString = useMemo(() => {
    let h = Number(form.time_hour || 0);
    if (form.time_ampm === "PM" && h < 12) h += 12;
    if (form.time_ampm === "AM" && h === 12) h = 0;
    return `${String(h).padStart(2, "0")}:${form.time_min || "00"}`;
  }, [form.time_hour, form.time_min, form.time_ampm]);

  // ISO for the ghost-pill heartbeat — only defined once the customer has
  // picked a date, so we don't pin the calendar until they've committed to
  // a day. `endAt` is derived from `hours` so managers see the full trip
  // window on their calendar, not just the pickup moment.
  const rideStartISO = useMemo(() => {
    if (!form.date) return null;
    try { return new Date(`${form.date}T${timeString}`).toISOString(); }
    catch { return null; }
  }, [form.date, timeString]);
  const rideEndISO = useMemo(() => {
    if (!rideStartISO || !(Number(form.hours) > 0)) return null;
    return new Date(new Date(rideStartISO).getTime() + Number(form.hours) * 3600 * 1000).toISOString();
  }, [rideStartISO, form.hours]);

  // 🕯️ Ghost-pill heartbeat while the customer picks pickup date/time.
  useBookingIntent({
    enabled: !confirmed,
    vehicleId: vehicle?.id,
    vehicleName: vehicle?.name,
    bookingType: "ride",
    startAt: rideStartISO,
    endAt: rideEndISO,
    stage: form.payment_method || null,
  });

  // Early blacklist detection — surface a friendly banner + freeze submit
  // the moment a barred email or phone is typed.
  const bl = useBlacklistCheck(form.email, form.phone);

  const missingFields = useMemo(() => {
    const missing = [];
    if (!form.full_name) missing.push("Full name");
    if (!form.phone) missing.push("Phone");
    if (!form.pickup) missing.push("Pickup");
    if (!form.dropoff) missing.push("Drop-off");
    if (!form.date) missing.push("Date");
    if (!form.time_hour || !form.time_min || !form.time_ampm) missing.push("Time");
    if (!(Number(form.hours) > 0)) missing.push("Hours");
    return missing;
  }, [form]);

  const canSubmit = useMemo(() => {
    return form.pickup && form.dropoff && form.date
      && form.time_hour && form.time_min && form.time_ampm
      && Number(form.hours) > 0
      && form.full_name && form.phone;
  }, [form]);

  const submit = async (e) => {
    e.preventDefault();
    if (bl.blocked) {
      toast.error("This email / phone can't submit a reservation. Please contact us directly.");
      return;
    }
    if (!canSubmit) {
      toast.error(`Please fill: ${missingFields.join(", ")}`);
      return;
    }
    setSubmitting(true);
    // Show a "sending" toast so the user gets immediate visual feedback
    // if the network takes a moment (was previously silent — looked broken).
    const sendingToast = toast.loading(
      form.payment_method === "cash" ? "Reserving your ride…" : "Preparing checkout…"
    );
    try {
      const pickup_dt = new Date(`${form.date}T${timeString}`).toISOString();
      const { data: booking } = await api.post("/bookings/ride", {
        vehicle_id: vehicle.id,
        pickup: form.pickup,
        dropoff: form.dropoff,
        pickup_datetime: pickup_dt,
        passenger_count: Number(form.passengers),
        estimated_duration_hours: Number(form.hours),
        special_requests: form.notes,
        trip_purpose: form.trip_purpose,
        full_name: form.full_name,
        email: form.email || undefined,
        phone: form.phone,
        payment_method: form.payment_method,
        promo_code: promoCode,
        // Marketing tag from the referring landing page's `?promo=…` slug.
        promo_channel: promoChannel || null,
        addons: addonsPayload,
        smoking_fee_accepted: form.smoking_fee_accepted,
      });
      toast.dismiss(sendingToast);
      // Cash + manual peer-to-peer methods skip Stripe redirect — driver /
      // manager collects out-of-band and staff verifies later.
      const MANUAL = ["cash", "cashapp_manual", "apple_pay_manual", "zelle"];
      if (MANUAL.includes(form.payment_method)) {
        setConfirmed(booking);
        toast.success("Reservation received — a manager will confirm within 24h.");
        setSubmitting(false);
        return;
      }
      try {
        const { data: pay } = await api.post("/payments/checkout", {
          booking_id: booking.id,
          origin_url: window.location.origin,
          payment_method: form.payment_method === "cashapp" ? "cashapp" : "card",
          amount_mode: form.payment_amount_mode || "full",
        });
        window.location.href = pay.url;
      } catch (payErr) {
        toast.error(
          payErr?.response?.data?.detail ||
          "Reservation saved, but online deposit is temporarily unavailable. A manager will reach out shortly.",
        );
        setConfirmed(booking);
        setSubmitting(false);
      }
    } catch (e) {
      toast.dismiss(sendingToast);
      const msg = e?.response?.data?.detail || e?.message ||
        "Could not create booking — check your internet connection and try again.";
      toast.error(String(msg));
      setSubmitting(false);
    }
  };

  if (!vehicle) {
    return <div className="h-96 flex items-center justify-center">
      <div className="w-10 h-10 rounded-full border-2 border-gold border-t-transparent animate-spin" />
    </div>;
  }

  if (confirmed) {
    return (
      <section data-testid="book-ride-confirmed" className="mx-auto max-w-2xl px-5 sm:px-8 py-16">
        <div className="glass rounded-3xl p-8 space-y-4 border border-lime-400/40">
          <div className="label-caps text-lime-400 text-xs">Reservation received</div>
          <h1 className="font-serif text-3xl">
            {confirmed.payment_method === "cash"
              ? `You're on the schedule — pay $${(confirmed.amount || 0).toFixed(2)} at pickup`
              : "Reservation saved"}
          </h1>
          <div className="text-sm text-white/70 space-y-1.5">
            <div>Reference: <span className="text-electric font-mono">{confirmed.id}</span></div>
            <div>Vehicle: {confirmed.vehicle_name}</div>
            <div>Pickup: {new Date(confirmed.pickup_datetime).toLocaleString()}</div>
            {confirmed.payment_method === "cash" && (
              <div className="mt-3 rounded-xl bg-lime-400/10 border border-lime-400/30 p-3 text-xs text-lime-200">
                Please have exact cash ready. Your driver will collect at pickup — no online deposit required.
              </div>
            )}
          </div>
          <p className="text-xs text-white/50">
            A manager will personally confirm within 24 hours. You'll get a text once your driver is assigned.
          </p>
          <button onClick={() => navigate("/")}
            className="btn-gold rounded-full px-6 py-2 text-xs">Back to home</button>
        </div>
      </section>
    );
  }

  return (
    <section data-testid={TID.bookRide.root} className="mx-auto max-w-7xl px-5 sm:px-8 py-12">
      <div className="label-caps text-gold mb-2">Chauffeur booking</div>
      <h1 className="font-serif text-4xl sm:text-5xl mb-4">{vehicle.name}</h1>

      {estimate?.promo_applied === "maybach250" && (
        <div
          data-testid="ride-promo-banner"
          className="glass rounded-2xl border border-cyan/40 bg-cyan/5 p-4 mb-6 flex items-start gap-3"
        >
          <Sparkles size={20} className="text-cyan flex-shrink-0 mt-0.5" />
          <div className="text-sm">
            <div className="label-caps text-cyan text-[10px] mb-0.5">Maybach special applied</div>
            <div className="font-serif text-lg leading-tight">
              $250/hour · 3-hour minimum
            </div>
            <div className="text-xs text-white/60 mt-0.5">
              Promo rate is locked to this reservation because you arrived from the flyer.
            </div>
          </div>
        </div>
      )}

      {event && (
        <div data-testid="book-ride-event-banner"
          className="glass rounded-2xl border border-gold/40 bg-gold/5 p-4 mb-6 flex items-start gap-3">
          <CalendarDays size={20} className="text-gold flex-shrink-0 mt-0.5" />
          <div className="text-sm">
            <div className="label-caps text-gold text-[10px] mb-0.5">Booking for event</div>
            <div className="font-serif text-lg leading-tight">{event.title}</div>
            <div className="text-xs text-white/60 mt-0.5">
              {new Date(event.event_date).toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
              {event.location && <> · {event.location}</>}
            </div>
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-[1.2fr_1fr] gap-10">
        <form onSubmit={submit} className="glass rounded-3xl p-6 sm:p-8 space-y-6">
          <div className="grid sm:grid-cols-3 gap-4">
            <FieldRow label="Full name" icon={User}>
              <input
                data-testid="ride-full-name"
                value={form.full_name}
                onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                placeholder="Alex Johnson"
                className="input"
                required
              />
            </FieldRow>
            <FieldRow label="Email (optional)" icon={Mail}>
              <input type="email"
                data-testid="ride-email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="you@example.com (optional)"
                className="input"
              />
            </FieldRow>
            <FieldRow label="Phone" icon={Phone}>
              <input type="tel"
                data-testid="ride-phone"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                placeholder="+1 917…"
                className="input"
                required
              />
            </FieldRow>
            {bl.blocked && (
              <div
                data-testid="ride-blacklist-notice"
                className="sm:col-span-2 rounded-2xl border border-red-500/50 bg-red-500/[0.08] p-4 text-[12px] text-red-100"
              >
                <div className="label-caps text-red-300 text-[10px] mb-1.5">Reservation not accepted</div>
                <div className="text-white/90">
                  This email / phone can't submit a reservation right now.
                </div>
                {bl.reason && (
                  <div className="mt-2 rounded-lg bg-black/25 border border-red-500/25 px-3 py-2 text-white/85">
                    <span className="label-caps text-[9px] text-red-300">Reason&nbsp;</span>{bl.reason}
                  </div>
                )}
                <div className="text-white/60 text-[11px] mt-2">
                  Please contact us at <span className="text-white">pay@plenty-rydez.com</span> or <span className="text-white">929-899-3467</span> if you think this is a mistake.
                </div>
              </div>
            )}
          </div>
          <FieldRow label="Pickup" icon={MapPin}>
            <input
              data-testid={TID.bookRide.pickup}
              value={form.pickup}
              onChange={(e) => setForm({ ...form, pickup: e.target.value })}
              placeholder="e.g. 432 Park Ave, New York, NY"
              className="input"
              required
            />
          </FieldRow>
          <FieldRow label="Trip purpose" icon={StickyNote}>
            <select
              data-testid="ride-trip-purpose"
              value={form.trip_purpose}
              onChange={(e) => setForm({ ...form, trip_purpose: e.target.value })}
              className="input"
            >
              <option value="airport">Airport transfer (JFK · LGA · EWR)</option>
              <option value="hourly">Hourly / point-to-point</option>
              <option value="corporate">Corporate / executive</option>
              <option value="photoshoot">Photoshoot / Production</option>
              <option value="wedding">Wedding</option>
              <option value="prom">Prom</option>
              <option value="concert">Concert</option>
              <option value="sporting_event">Sporting event</option>
              <option value="nightlife">Nightlife</option>
              <option value="funeral">Funeral</option>
              <option value="medical">Medical transportation</option>
              <option value="cruise">Cruise terminal</option>
              <option value="other">Other</option>
            </select>
          </FieldRow>
          <FieldRow label="Drop-off" icon={MapPin}>
            <input
              data-testid={TID.bookRide.dropoff}
              value={form.dropoff}
              onChange={(e) => setForm({ ...form, dropoff: e.target.value })}
              placeholder="e.g. JFK Terminal 4"
              className="input"
              required
            />
          </FieldRow>
          <div className="grid grid-cols-2 gap-4">
            <FieldRow label="Date" icon={Calendar}>
              <input type="date"
                data-testid={`${TID.bookRide.datetime}-date`}
                value={form.date}
                min={new Date().toISOString().split("T")[0]}
                onChange={(e) => setForm({ ...form, date: e.target.value })}
                className="input" required />
            </FieldRow>
            <FieldRow label="Time" icon={Clock}>
              <div className="grid grid-cols-3 gap-1.5">
                <select
                  data-testid={`${TID.bookRide.datetime}-hour`}
                  value={form.time_hour}
                  onChange={(e) => setForm({ ...form, time_hour: e.target.value })}
                  className="input" required>
                  {[1,2,3,4,5,6,7,8,9,10,11,12].map((h) => (
                    <option key={h} value={String(h)}>{h}</option>
                  ))}
                </select>
                <select
                  data-testid={`${TID.bookRide.datetime}-min`}
                  value={form.time_min}
                  onChange={(e) => setForm({ ...form, time_min: e.target.value })}
                  className="input" required>
                  {["00","15","30","45"].map((m) => (
                    <option key={m} value={m}>:{m}</option>
                  ))}
                </select>
                <select
                  data-testid={`${TID.bookRide.datetime}-ampm`}
                  value={form.time_ampm}
                  onChange={(e) => setForm({ ...form, time_ampm: e.target.value })}
                  className="input" required>
                  <option value="AM">AM</option>
                  <option value="PM">PM</option>
                </select>
              </div>
            </FieldRow>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <FieldRow label="Passengers" icon={Users}>
              <input type="number" min={1} max={vehicle.capacity}
                data-testid={TID.bookRide.passengers}
                value={form.passengers}
                onChange={(e) => setForm({ ...form, passengers: e.target.value })}
                className="input" />
            </FieldRow>
            <FieldRow label={`Estimated hours · min ${estimate?.min_hours || vehicle.min_hours || 3}`} icon={Clock}>
              <input type="number" min={estimate?.min_hours || vehicle.min_hours || 3} step={0.5}
                data-testid={TID.bookRide.hours}
                value={form.hours}
                onChange={(e) => setForm({ ...form, hours: e.target.value })}
                className="input" />
            </FieldRow>
          </div>
          <FieldRow label="Special requests" icon={StickyNote}>
            <textarea
              data-testid={TID.bookRide.notes}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="Meet & greet, car seat, water preferences…"
              rows={3}
              className="input"
            />
          </FieldRow>
          <AddonsPicker
            catalog={addonsCatalog}
            selected={selectedAddons}
            onChange={setSelectedAddons}
          />
          <SmokingFeeToggle
            accepted={form.smoking_fee_accepted}
            onChange={(v) => setForm({ ...form, smoking_fee_accepted: v })}
            fee={estimate?.smoking_fee || 200}
            testId="ride-smoking-fee"
          />
          <FieldRow label="Payment method" icon={StickyNote}>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              <button
                type="button"
                data-testid="ride-pay-card"
                onClick={() => setForm({ ...form, payment_method: "card" })}
                className={`rounded-full py-3 px-3 text-[11px] label-caps border transition ${
                  form.payment_method === "card"
                    ? "bg-electric/20 border-electric text-electric"
                    : "bg-white/5 border-white/10 text-white/60 hover:text-white"
                }`}
              >
                Card · deposit
              </button>
              <button
                type="button"
                data-testid="ride-pay-cashapp"
                onClick={() => setForm({ ...form, payment_method: "cashapp" })}
                className={`rounded-full py-3 px-3 text-[11px] label-caps border transition ${
                  form.payment_method === "cashapp"
                    ? "bg-lime-400/20 border-lime-400 text-lime-300"
                    : "bg-white/5 border-white/10 text-white/60 hover:text-white"
                }`}
              >
                Cash App Pay
              </button>
              <button
                type="button"
                data-testid="ride-pay-cash"
                onClick={() => setForm({ ...form, payment_method: "cash" })}
                className={`rounded-full py-3 px-3 text-[11px] label-caps border transition ${
                  form.payment_method === "cash"
                    ? "bg-lime-400/20 border-lime-400 text-lime-300"
                    : "bg-white/5 border-white/10 text-white/60 hover:text-white"
                }`}
              >
                Cash at pickup
              </button>
              <button
                type="button"
                data-testid="ride-pay-apple_pay_manual"
                onClick={() => setForm({ ...form, payment_method: "apple_pay_manual" })}
                className={`rounded-full py-3 px-3 text-[11px] label-caps border transition ${
                  form.payment_method === "apple_pay_manual"
                    ? "bg-lime-400/20 border-lime-400 text-lime-300"
                    : "bg-white/5 border-white/10 text-white/60 hover:text-white"
                }`}
              >
                Apple Pay
              </button>
              <button
                type="button"
                data-testid="ride-pay-zelle"
                onClick={() => setForm({ ...form, payment_method: "zelle" })}
                className={`rounded-full py-3 px-3 text-[11px] label-caps border transition ${
                  form.payment_method === "zelle"
                    ? "bg-lime-400/20 border-lime-400 text-lime-300"
                    : "bg-white/5 border-white/10 text-white/60 hover:text-white"
                }`}
              >
                Zelle
              </button>
            </div>
            {["card", "cashapp"].includes(form.payment_method) && estimate?.total ? (() => {
              const modes = [
                { key: "full",    label: "Full amount",     amount: estimate.total, desc: "Pay the entire booking now" },
                { key: "deposit", label: "Deposit only",    amount: estimate.deposit || estimate.total * 0.5, desc: "Refundable hold, rest at pickup", hide: !(estimate.deposit && estimate.deposit > 0) },
                { key: "half",    label: "50% now",         amount: +(estimate.total / 2).toFixed(2), desc: "Split the total in half — rest due at pickup" },
              ].filter((m) => !m.hide);
              const active = form.payment_amount_mode || "full";
              const activeAmt = modes.find((m) => m.key === active)?.amount || estimate.total;
              const fee = +(activeAmt * 0.029 + 0.30).toFixed(2);
              const totalWithFee = +(activeAmt + fee).toFixed(2);
              return (
                <div data-testid="ride-pay-amount-picker" className="mt-3 space-y-2">
                  <div className="label-caps text-[9px] text-white/50">How much to pay today</div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                    {modes.map((m) => (
                      <button
                        type="button"
                        key={m.key}
                        data-testid={`ride-pay-mode-${m.key}`}
                        onClick={() => setForm({ ...form, payment_amount_mode: m.key })}
                        className={`text-left rounded-2xl border p-3 transition ${
                          active === m.key
                            ? "border-cyan bg-cyan/10 shadow-[0_0_20px_rgba(34,211,238,0.15)]"
                            : "border-white/12 hover:border-white/30"
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-white">{m.label}</span>
                          <span className={`text-xs ${active === m.key ? "text-cyan font-semibold" : "text-white/60"}`}>${m.amount.toFixed(2)}</span>
                        </div>
                        <div className="text-[10px] text-white/40 mt-0.5">{m.desc}</div>
                      </button>
                    ))}
                  </div>
                  <div
                    data-testid={`ride-fee-note-${form.payment_method}`}
                    className="rounded-xl border border-cyan/30 bg-cyan/[0.05] px-3 py-2.5 text-[11px] text-white/85 leading-relaxed"
                  >
                    <span className="label-caps text-cyan text-[10px] mr-1.5">
                      {form.payment_method === "cashapp" ? "Cash App Pay" : "Card"} · charge today
                    </span>
                    ${activeAmt.toFixed(2)} + ${fee.toFixed(2)} fee (2.9% + $0.30) = <span className="text-cyan font-semibold">${totalWithFee.toFixed(2)}</span>
                    {active !== "full" && estimate.total > activeAmt && (
                      <div className="text-white/50 mt-1">
                        Remaining <span className="text-white">${(estimate.total - activeAmt).toFixed(2)}</span> due at pickup.
                      </div>
                    )}
                  </div>
                </div>
              );
            })() : null}
            {form.payment_method === "apple_pay_manual" && (
              <div data-testid="ride-manual-instr-apple" className="mt-2 rounded-xl border border-lime-400/40 bg-lime-400/[0.06] px-3 py-2.5 text-[11px] text-white/85 leading-relaxed">
                Send your deposit or full payment via <b>Apple Pay / Apple Cash</b> to
                <span className="text-lime-300 font-mono ml-1">718-840-9084</span> · Note your name so we can match it.
              </div>
            )}
            {form.payment_method === "zelle" && (
              <div data-testid="ride-manual-instr-zelle" className="mt-2 rounded-xl border border-lime-400/40 bg-lime-400/[0.06] px-3 py-2.5 text-[11px] text-white/85 leading-relaxed">
                Send your deposit or full payment via <b>Zelle</b> to
                <span className="text-lime-300 font-mono ml-1">718-840-9084</span> · Note your name so we can match it.
              </div>
            )}
          </FieldRow>
          <button
            data-testid={TID.bookRide.submit}
            disabled={!canSubmit || submitting}
            onClick={(e) => {
              if (!canSubmit) {
                e.preventDefault();
                toast.error(`Please fill: ${missingFields.join(", ")}`);
              }
            }}
            className="btn-gold w-full py-3 rounded-full text-sm disabled:opacity-50"
          >
            {submitting
              ? (["cash","cashapp_manual","apple_pay_manual","zelle"].includes(form.payment_method) ? "Sending reservation…" : "Preparing payment…")
              : !canSubmit
                ? `Fill in ${missingFields.length} field${missingFields.length === 1 ? "" : "s"} to reserve`
                : form.payment_method === "cash"
                  ? `Reserve · pay $${estimate?.total?.toFixed(2) || "…"} at pickup`
                  : ["cashapp_manual","apple_pay_manual","zelle"].includes(form.payment_method)
                    ? `Reserve · then send $${estimate?.total?.toFixed(2) || "…"}`
                    : `Continue to payment · $${estimate?.total?.toFixed(2) || "…"}`}
          </button>
          {!canSubmit && missingFields.length > 0 && (
            <div data-testid="ride-missing-hint"
              className="text-[11px] text-amber-300 bg-amber-400/10 border border-amber-400/30 rounded-xl px-3 py-2 mt-2">
              Missing: <span className="font-medium">{missingFields.join(" · ")}</span>
            </div>
          )}
        </form>

        <aside data-testid={TID.bookRide.estimate} className="space-y-6">
          <div className="rounded-3xl overflow-hidden border border-white/10">
            <VehicleGallery
              images={vehicle.images}
              image_url={vehicle.image_url}
              alt={vehicle.name}
              aspect="aspect-[16/10]"
              enableLightbox={true}
              testId="book-ride-gallery"
            />
          </div>
          <div className="glass rounded-3xl p-6 space-y-3">
            <div className="label-caps text-gold">Estimate</div>
            <Row k="Base fee" v={`$${(estimate?.base_fee ?? 25).toFixed(2)}`} />
            <Row k={`Requested duration`} v={`${form.hours} hrs`} />
            <Row k={`Billed (min ${estimate?.min_hours || vehicle.min_hours || 3} hrs)`}
                 v={`${estimate?.billed_hours ?? Math.max(Number(form.hours) || 0, estimate?.min_hours || vehicle.min_hours || 3)} hrs @ $${estimate?.hourly_rate ?? vehicle.hourly_rate}/hr`} />
            {estimate?.addons?.length > 0 && (
              <div data-testid="ride-estimate-addons" className="pt-1 space-y-1">
                <div className="label-caps text-[10px] text-electric">Add-ons</div>
                {estimate.addons.map((a) => (
                  <Row
                    key={a.item_id}
                    k={<span className="text-white/70">{a.name} × {a.qty}</span>}
                    v={<span className="text-white">${a.line_total.toFixed(2)}</span>}
                  />
                ))}
                <Row
                  k={<span className="text-white/80">Add-ons subtotal</span>}
                  v={<span className="text-electric" data-testid="ride-estimate-addons-total">${(estimate.addons_total || 0).toFixed(2)}</span>}
                />
              </div>
            )}
            {estimate?.smoking_fee > 0 && (
              <Row
                k={<span className="text-amber-300">Smoking permitted fee</span>}
                v={<span className="text-amber-300" data-testid="ride-estimate-smoking-fee">${estimate.smoking_fee.toFixed(2)}</span>}
              />
            )}
            <div className="border-t border-white/10 pt-3" />
            <Row k={<span className="text-white">Total</span>}
                 v={<span data-testid={TID.bookRide.total} className="text-gold text-lg">${estimate?.total?.toFixed(2) || "—"}</span>} />
            {estimate && estimate.billed_hours > (Number(form.hours) || 0) && (
              <div className="text-xs text-electric pt-1">
                Below the {estimate?.min_hours || vehicle.min_hours || 3}-hour minimum — billed at {estimate.billed_hours} hrs.
              </div>
            )}
            <p className="text-xs text-white/50 pt-2">
              Final fare may adjust for waiting, tolls or route changes. Gratuity not included.
            </p>
          </div>
        </aside>
      </div>
      <StyleTag />
    </section>
  );
}

const FieldRow = ({ label, icon: Icon, children }) => (
  <label className="block">
    <div className="label-caps text-white/60 mb-2 flex items-center gap-1.5">
      {Icon && <Icon size={12} className="text-gold" />}{label}
    </div>
    {children}
  </label>
);

/* ---------- Add-ons picker ---------- */
// Categorized qty stepper. Renders one card per active category with an active
// item list inside. Items with qty > 0 highlight cyan. Empty categories collapse
// to a small "no items yet" note so the picker never looks broken when a
// manager has cleared out a category.
function AddonsPicker({ catalog, selected, onChange }) {
  if (!catalog || catalog.length === 0) return null;
  const activeCats = catalog.filter((c) => (c.items || []).length > 0);
  if (activeCats.length === 0) return null;
  const bump = (itemId, delta) => {
    const cur = Number(selected[itemId] || 0);
    const next = Math.max(0, cur + delta);
    const nextMap = { ...selected };
    if (next === 0) delete nextMap[itemId];
    else nextMap[itemId] = next;
    onChange(nextMap);
  };
  const totalPicked = Object.values(selected || {}).reduce((s, q) => s + Number(q || 0), 0);
  return (
    <div data-testid="ride-addons-picker" className="rounded-2xl border border-electric/20 bg-electric/[0.04] p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div className="label-caps text-electric text-[11px] flex items-center gap-1.5">
          <Sparkles size={12} /> In-car add-ons {totalPicked > 0 && (
            <span className="ml-1 px-1.5 py-0.5 rounded-full bg-electric text-black text-[10px]" data-testid="ride-addons-count">{totalPicked}</span>
          )}
        </div>
        <span className="text-[10px] text-white/40">Optional · added at pickup</span>
      </div>
      <div className="text-[11px] text-white/60 -mt-1">
        Flowers, candy, chips, drinks and more — pre-order and we'll have it waiting.
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {activeCats.map((cat) => {
          const Icon = ADDON_ICON[cat.slug] || Sparkles;
          return (
            <div key={cat.id} data-testid={`ride-addons-cat-${cat.slug}`} className="rounded-xl bg-white/[0.03] border border-white/10 p-3 space-y-2">
              <div className="flex items-center gap-2 text-white/85">
                <Icon size={14} className="text-electric" />
                <span className="text-xs font-medium">{cat.label}</span>
              </div>
              <div className="space-y-1.5">
                {cat.items.map((it) => {
                  const qty = Number(selected[it.id] || 0);
                  return (
                    <div key={it.id} data-testid={`ride-addons-item-${it.id}`}
                      className={`flex items-center justify-between gap-2 rounded-lg px-2.5 py-2 border transition ${
                        qty > 0 ? "border-electric/40 bg-electric/[0.06]" : "border-white/10 bg-transparent"
                      }`}>
                      <div className="flex-1 min-w-0">
                        <div className="text-xs text-white truncate">{it.name}</div>
                        <div className="text-[10px] text-white/50">${Number(it.price || 0).toFixed(2)}</div>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <button
                          type="button"
                          data-testid={`ride-addons-dec-${it.id}`}
                          onClick={() => bump(it.id, -1)}
                          disabled={qty === 0}
                          aria-label={`Remove one ${it.name}`}
                          className="w-7 h-7 rounded-full border border-white/15 flex items-center justify-center text-white/70 hover:text-white hover:border-electric/40 disabled:opacity-30"
                        >
                          <Minus size={12} />
                        </button>
                        <span data-testid={`ride-addons-qty-${it.id}`} className="min-w-[1.25rem] text-center text-sm text-white">{qty}</span>
                        <button
                          type="button"
                          data-testid={`ride-addons-inc-${it.id}`}
                          onClick={() => bump(it.id, +1)}
                          aria-label={`Add one ${it.name}`}
                          className="w-7 h-7 rounded-full border border-electric/40 bg-electric/10 flex items-center justify-center text-electric hover:bg-electric/20"
                        >
                          <Plus size={12} />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

const Row = ({ k, v }) => (
  <div className="flex items-center justify-between text-sm">
    <span className="text-white/60">{k}</span>
    <span className="text-white">{v}</span>
  </div>
);

const StyleTag = () => (
  <style>{`
    .input {
      background: #121214; border: 1px solid rgba(255,255,255,0.08);
      color: #fff; border-radius: 12px; padding: 12px 14px;
      width: 100%; font-size: 14px;
      transition: border-color 0.2s;
    }
    .input:focus { outline: none; border-color: #D4AF37; box-shadow: 0 0 0 3px rgba(212,175,55,0.15); }
    .input::-webkit-calendar-picker-indicator { filter: invert(1) opacity(0.6); }
  `}</style>
);
